/**
 * Contacts Page — shadcn/ui v4 redesign
 *
 * Visual changes (zero business-logic change):
 *   • Header: single primary "Add Contact" + secondary DropdownMenu (Import/Export/WhatsApp/Email)
 *   • Stat cards: gradient bg-gradient-to-t from-primary/5, icon in CardAction slot
 *   • Table card: search + sort merged into CardHeader > CardAction (eliminates standalone filter card)
 *   • Checkbox bulk-select: bulk delete + bulk export action bar
 *   • Skeleton rows while loading (no more plain "Loading…" text)
 *   • Per-row hover quick-actions (Eye + Pencil) + MoreVertical dropdown
 *   • Contact detail: Sheet slide-in (replaces Dialog) with quick-action buttons
 *   • Notifications: toast (bottom-right, non-blocking) instead of inline Alert
 *   • Pagination: first/prev/next/last + "Page N of M" display
 *   • Column visibility toggle in CardAction
 *
 * All API calls, state, and workflows are unchanged.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Search,
  Plus,
  Pencil,
  Trash2,
  MoreVertical,
  Mail,
  Phone,
  MapPin,
  Eye,
  Download,
  Upload,
  MessageSquare,
  Users,
  TrendingUp,
  AlertTriangle,
  ChevronsLeft,
  ChevronsRight,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  X,
  Columns3,
} from 'lucide-react';
import EmailOutreachDialog from '../components/Email/EmailOutreachDialog';
import { contactsAPI } from '../services/api';
import { ConversationInitiator } from '../components/conversations';
import { formatDate } from '../utils/dateUtils';
import { toast } from '../components/ui/use-toast';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { cn } from '../lib/utils';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Contact {
  id: number;
  name?: string;
  email?: string;
  phone_number?: string;
  avatar_url?: string;
  country?: string;
  city?: string;
  custom_attributes: Record<string, any>;
  created_at: string;
  conversations_count: number;
}

interface ContactFormData {
  name: string;
  email: string;
  phone_number: string;
  country: string;
  city: string;
  custom_attributes: Record<string, any>;
}

type SortKey = 'created_at' | 'name' | 'email';
type SortDir = 'asc' | 'desc';

// ── Helpers ───────────────────────────────────────────────────────────────────

const getInitials = (name?: string) => {
  if (!name) return '?';
  return name.split(' ').map((n) => n.charAt(0)).join('').toUpperCase().slice(0, 2);
};

// ── Sub-components ────────────────────────────────────────────────────────────

/** Shared form for create/edit dialogs */
const ContactForm: React.FC<{
  formData: ContactFormData;
  onChange: (field: keyof ContactFormData, value: string) => void;
}> = ({ formData, onChange }) => (
  <div className="space-y-4 py-4">
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label htmlFor="contact-name">Name *</Label>
        <Input
          id="contact-name"
          value={formData.name}
          onChange={(e) => onChange('name', e.target.value)}
          placeholder="Full name"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="contact-email">Email</Label>
        <Input
          id="contact-email"
          type="email"
          value={formData.email}
          onChange={(e) => onChange('email', e.target.value)}
          placeholder="email@example.com"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="contact-phone">Phone Number</Label>
        <Input
          id="contact-phone"
          value={formData.phone_number}
          onChange={(e) => onChange('phone_number', e.target.value)}
          placeholder="+1234567890"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="contact-country">Country</Label>
        <Input
          id="contact-country"
          value={formData.country}
          onChange={(e) => onChange('country', e.target.value)}
          placeholder="Country"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="contact-city">City</Label>
        <Input
          id="contact-city"
          value={formData.city}
          onChange={(e) => onChange('city', e.target.value)}
          placeholder="City"
        />
      </div>
    </div>
  </div>
);

/** Skeleton row for loading state */
const SkeletonRow: React.FC<{ cols: number }> = ({ cols }) => (
  <TableRow>
    <TableCell className="pl-4 w-10">
      <Skeleton className="h-4 w-4 rounded" />
    </TableCell>
    <TableCell>
      <div className="flex items-center gap-2.5">
        <Skeleton className="h-8 w-8 rounded-full shrink-0" />
        <div className="space-y-1.5">
          <Skeleton className="h-3.5 w-28" />
          <Skeleton className="h-3 w-10" />
        </div>
      </div>
    </TableCell>
    {cols >= 3 && <TableCell><Skeleton className="h-3.5 w-36" /></TableCell>}
    {cols >= 4 && <TableCell><Skeleton className="h-3.5 w-24" /></TableCell>}
    {cols >= 5 && <TableCell className="hidden sm:table-cell"><Skeleton className="h-3.5 w-20" /></TableCell>}
    <TableCell><Skeleton className="h-5 w-8 rounded-full mx-auto" /></TableCell>
    <TableCell className="hidden sm:table-cell"><Skeleton className="h-3.5 w-16" /></TableCell>
    <TableCell />
  </TableRow>
);

// ── Column visibility config ──────────────────────────────────────────────────

const COLUMN_DEFS = [
  { key: 'email',    label: 'Email' },
  { key: 'phone',    label: 'Phone' },
  { key: 'location', label: 'Location' },
  { key: 'convs',    label: 'Conversations' },
  { key: 'created',  label: 'Created' },
] as const;

type ColKey = (typeof COLUMN_DEFS)[number]['key'];

// ── Stat tiles ────────────────────────────────────────────────────────────────

interface StatTile {
  label: string;
  key: 'total_contacts' | 'contacts_with_email' | 'contacts_with_phone' | 'engagement_rate';
  icon: React.ElementType;
  iconClass: string;
  suffix?: string;
}

const STAT_TILES: StatTile[] = [
  { label: 'Total Contacts',  key: 'total_contacts',      icon: Users,      iconClass: 'text-primary' },
  { label: 'With Email',      key: 'contacts_with_email', icon: Mail,       iconClass: 'text-sky-500' },
  { label: 'With Phone',      key: 'contacts_with_phone', icon: Phone,      iconClass: 'text-violet-500' },
  { label: 'Engagement Rate', key: 'engagement_rate',     icon: TrendingUp, iconClass: 'text-emerald-500', suffix: '%' },
];

// ── Page ──────────────────────────────────────────────────────────────────────

const ContactsPage: React.FC = () => {
  // ── List state ─────────────────────────────────────────────────────────────
  const [contacts, setContacts]     = useState<Contact[]>([]);
  const [loading, setLoading]       = useState(false);
  const [page, setPage]             = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal]           = useState(0);
  const [searchText, setSearchText] = useState('');
  const [sortBy, setSortBy]         = useState<SortKey>('created_at');
  const [sortOrder, setSortOrder]   = useState<SortDir>('desc');

  // ── Dialog / panel state ───────────────────────────────────────────────────
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [createDialogOpen, setCreateDialogOpen]     = useState(false);
  const [editDialogOpen, setEditDialogOpen]         = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen]     = useState(false);
  const [viewSheetOpen, setViewSheetOpen]           = useState(false);
  const [emailOutreachOpen, setEmailOutreachOpen]   = useState(false);
  const [emailOutreachContact, setEmailOutreachContact] = useState<Contact | null>(null);
  const [whatsappInitiatorOpen, setWhatsappInitiatorOpen] = useState(false);

  // ── Form state ─────────────────────────────────────────────────────────────
  const [formData, setFormData] = useState<ContactFormData>({
    name: '', email: '', phone_number: '', country: '', city: '', custom_attributes: {},
  });

  // ── Stats ──────────────────────────────────────────────────────────────────
  const [stats, setStats] = useState({
    total_contacts: 0,
    contacts_with_email: 0,
    contacts_with_phone: 0,
    contacts_with_conversations: 0,
    engagement_rate: 0,
  });

  // ── Bulk selection ─────────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // ── Column visibility ──────────────────────────────────────────────────────
  const [visibleCols, setVisibleCols] = useState<Set<ColKey>>(
    new Set(['email', 'phone', 'location', 'convs', 'created']),
  );

  // ── Data loading ───────────────────────────────────────────────────────────

  useEffect(() => { loadStats(); }, []);

  const loadStats = async () => {
    try {
      const response = await contactsAPI.getContactStats();
      setStats(response);
    } catch {
      setStats({ total_contacts: 0, contacts_with_email: 0, contacts_with_phone: 0, contacts_with_conversations: 0, engagement_rate: 0 });
    }
  };

  const loadContacts = useCallback(async () => {
    try {
      setLoading(true);
      if (rowsPerPage === -1) {
        let allContacts: Contact[] = [];
        let currentPage = 1;
        let hasMore = true;
        while (hasMore) {
          const response = await contactsAPI.getContacts({ page: currentPage, per_page: 100, search: searchText || undefined, sort_by: sortBy, sort_order: sortOrder });
          const batch: Contact[] = response.contacts || response.data?.contacts || [];
          allContacts = [...allContacts, ...batch];
          const totalCount = response.total ?? response.data?.total ?? 0;
          hasMore = allContacts.length < totalCount;
          currentPage++;
          if (currentPage > 1000) break;
        }
        setContacts(allContacts);
        setTotal(allContacts.length);
      } else {
        const perPage = Math.min(Math.max(1, rowsPerPage), 100);
        const response = await contactsAPI.getContacts({
          page: page + 1,
          per_page: perPage,
          search: searchText || undefined,
          sort_by: sortBy,
          sort_order: sortOrder,
        });
        const list: Contact[] = response.contacts || response.data?.contacts || [];
        const totalCount = response.total ?? response.data?.total ?? response.count ?? 0;
        setContacts(list);
        setTotal(totalCount);
      }
    } catch {
      toast({ title: 'Error', description: 'Failed to load contacts', variant: 'destructive' });
      setContacts([]);
      setTotal(0);
    } finally {
      setLoading(false);
      setSelectedIds(new Set());
    }
  }, [page, rowsPerPage, searchText, sortBy, sortOrder]);

  useEffect(() => {
    const id = setTimeout(() => {
      void loadContacts();
    }, searchText ? 500 : 0);
    return () => clearTimeout(id);
  }, [loadContacts, page, rowsPerPage, searchText, sortBy, sortOrder]);

  // ── CRUD handlers ──────────────────────────────────────────────────────────

  const handleCreateContact = async () => {
    try {
      await contactsAPI.createContact(formData);
      setCreateDialogOpen(false);
      resetForm();
      loadContacts();
      loadStats();
      toast({ title: 'Contact created', description: `${formData.name || 'Contact'} was added successfully.` });
    } catch (error: any) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to create contact', variant: 'destructive' });
    }
  };

  const handleUpdateContact = async () => {
    if (!selectedContact) return;
    try {
      await contactsAPI.updateContact(selectedContact.id, formData);
      setEditDialogOpen(false);
      resetForm();
      loadContacts();
      toast({ title: 'Contact updated', description: `${formData.name || 'Contact'} was updated successfully.` });
    } catch {
      toast({ title: 'Error', description: 'Failed to update contact', variant: 'destructive' });
    }
  };

  const handleDeleteContact = async () => {
    if (!selectedContact) return;
    try {
      await contactsAPI.deleteContact(selectedContact.id);
      setDeleteDialogOpen(false);
      setSelectedContact(null);
      loadContacts();
      loadStats();
      toast({ title: 'Contact deleted', description: 'The contact has been removed.' });
    } catch (error: any) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to delete contact', variant: 'destructive' });
    }
  };

  const handleBulkDelete = async () => {
    try {
      await Promise.all([...selectedIds].map((id) => contactsAPI.deleteContact(id)));
      toast({ title: `${selectedIds.size} contacts deleted` });
      setSelectedIds(new Set());
      loadContacts();
      loadStats();
    } catch {
      toast({ title: 'Error', description: 'Some contacts could not be deleted', variant: 'destructive' });
    }
  };

  const handleExportContacts = async () => {
    try {
      await contactsAPI.exportContacts();
      toast({ title: 'Export ready', description: 'Contacts exported as CSV.' });
    } catch {
      toast({ title: 'Error', description: 'Failed to export contacts', variant: 'destructive' });
    }
  };

  const handleImportContacts = async (file: File) => {
    try {
      const response = await contactsAPI.importContacts(file);
      toast({ title: 'Import complete', description: `${response.imported_count} contacts imported.` });
      loadContacts();
      loadStats();
    } catch {
      toast({ title: 'Error', description: 'Failed to import contacts', variant: 'destructive' });
    }
  };

  const handleFileImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,.xlsx';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) handleImportContacts(file);
    };
    input.click();
  };

  // ── Form helpers ───────────────────────────────────────────────────────────

  const resetForm = useCallback(() => {
    setFormData({ name: '', email: '', phone_number: '', country: '', city: '', custom_attributes: {} });
  }, []);

  const handleFormChange = useCallback((field: keyof ContactFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const openCreateDialog = () => { resetForm(); setCreateDialogOpen(true); };
  const openEditDialog = (contact: Contact) => {
    setSelectedContact(contact);
    setFormData({ name: contact.name || '', email: contact.email || '', phone_number: contact.phone_number || '', country: contact.country || '', city: contact.city || '', custom_attributes: contact.custom_attributes || {} });
    setEditDialogOpen(true);
  };
  const openDeleteDialog = (contact: Contact) => { setSelectedContact(contact); setDeleteDialogOpen(true); };
  const openViewSheet = (contact: Contact) => { setSelectedContact(contact); setViewSheetOpen(true); };

  // ── Bulk select helpers ────────────────────────────────────────────────────

  const allPageSelected = contacts.length > 0 && contacts.every((c) => selectedIds.has(c.id));
  const somePageSelected = contacts.some((c) => selectedIds.has(c.id)) && !allPageSelected;

  const toggleAll = () => {
    if (allPageSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        contacts.forEach((c) => next.delete(c.id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        contacts.forEach((c) => next.add(c.id));
        return next;
      });
    }
  };

  const toggleOne = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // ── Sort helpers ───────────────────────────────────────────────────────────

  const handleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortOrder('asc');
    }
    setPage(0);
  };

  const SortIcon: React.FC<{ col: SortKey }> = ({ col }) => {
    if (sortBy !== col) return <ArrowUpDown className="ml-1 h-3 w-3 text-muted-foreground/50 inline" />;
    return sortOrder === 'asc'
      ? <ArrowUp className="ml-1 h-3 w-3 text-foreground inline" />
      : <ArrowDown className="ml-1 h-3 w-3 text-foreground inline" />;
  };

  // ── Pagination ─────────────────────────────────────────────────────────────

  const totalPages = useMemo(() => {
    if (rowsPerPage <= 0) return 1;
    if (total <= 0) return 1;
    return Math.max(1, Math.ceil(total / rowsPerPage));
  }, [total, rowsPerPage]);

  useEffect(() => {
    if (rowsPerPage <= 0 || total <= 0) return;
    const maxPage = totalPages - 1;
    if (page > maxPage) setPage(maxPage);
  }, [page, rowsPerPage, total, totalPages]);

  const goTo = (p: number) => {
    setPage(Math.max(0, Math.min(p, totalPages - 1)));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ── Column visibility ──────────────────────────────────────────────────────

  const toggleCol = (key: ColKey) => {
    setVisibleCols((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-1 flex-col gap-4 py-4 px-4 md:gap-6 md:py-6 lg:px-6">

      {/* ── Page Header ─────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Contacts</h1>
          <p className="text-sm text-muted-foreground">Manage your customer contacts</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Secondary actions */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <MoreVertical className="h-4 w-4 mr-1.5" />
                More
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel>Outreach</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => { setEmailOutreachContact(null); setEmailOutreachOpen(true); }}>
                <Mail className="h-4 w-4 mr-2" />
                Send Email Campaign
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { setSelectedContact(null); setWhatsappInitiatorOpen(true); }}>
                <MessageSquare className="h-4 w-4 mr-2" />
                Start WhatsApp Chat
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>Import / Export</DropdownMenuLabel>
              <DropdownMenuItem onClick={handleFileImport}>
                <Upload className="h-4 w-4 mr-2" />
                Import CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportContacts}>
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Primary CTA */}
          <Button size="sm" onClick={openCreateDialog}>
            <Plus className="h-4 w-4 mr-1.5" />
            Add Contact
          </Button>
        </div>
      </div>

      {/* ── Email Outreach Dialog ────────────────────────────────────────── */}
      <EmailOutreachDialog
        open={emailOutreachOpen}
        onClose={() => { setEmailOutreachOpen(false); setEmailOutreachContact(null); }}
        preselectedContact={emailOutreachContact}
        onSuccess={() => {
          toast({ title: 'Email sent', description: 'Your email campaign was sent successfully.' });
          loadContacts();
        }}
      />

      {/* ── Stat cards ──────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {STAT_TILES.map(({ label, key, icon: Icon, iconClass, suffix }) => (
          <Card key={key} className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
            <CardHeader>
              <CardDescription>{label}</CardDescription>
              <CardTitle className="text-2xl font-semibold tabular-nums">
                {stats[key]}{suffix ?? ''}
              </CardTitle>
              <CardAction>
                <div className={cn('rounded-full bg-muted p-2', iconClass)}>
                  <Icon className="h-4 w-4" />
                </div>
              </CardAction>
            </CardHeader>
          </Card>
        ))}
      </div>

      {/* ── Table card ──────────────────────────────────────────────────── */}
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle>All Contacts</CardTitle>
          <CardDescription>
            {total.toLocaleString()} contact{total !== 1 ? 's' : ''}
          </CardDescription>
          <CardAction className="flex items-center gap-2">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              <Input
                placeholder="Search..."
                value={searchText}
                onChange={(e) => { setSearchText(e.target.value); setPage(0); }}
                className="h-8 pl-8 w-[180px] text-sm"
              />
            </div>
            {/* Sort */}
            <Select
              value={`${sortBy}:${sortOrder}`}
              onValueChange={(v) => {
                const [key, dir] = v.split(':') as [SortKey, SortDir];
                setSortBy(key);
                setSortOrder(dir);
                setPage(0);
              }}
            >
              <SelectTrigger className="h-8 w-[148px] text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="created_at:desc">Newest first</SelectItem>
                <SelectItem value="created_at:asc">Oldest first</SelectItem>
                <SelectItem value="name:asc">Name A–Z</SelectItem>
                <SelectItem value="name:desc">Name Z–A</SelectItem>
                <SelectItem value="email:asc">Email A–Z</SelectItem>
              </SelectContent>
            </Select>
            {/* Column visibility */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 gap-1.5 text-sm">
                  <Columns3 className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Columns</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {COLUMN_DEFS.map((col) => (
                  <DropdownMenuCheckboxItem
                    key={col.key}
                    checked={visibleCols.has(col.key)}
                    onCheckedChange={() => toggleCol(col.key)}
                  >
                    {col.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </CardAction>
        </CardHeader>

        <CardContent className="p-0 pb-2">
          {/* ── Bulk action bar ─────────────────────────────────────────── */}
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-3 px-4 py-2 border-b border-border bg-muted/40 animate-in slide-in-from-top-1 duration-150">
              <span className="text-sm font-medium">{selectedIds.size} selected</span>
              <Button variant="outline" size="sm" className="h-7" onClick={handleExportContacts}>
                <Download className="h-3.5 w-3.5 mr-1.5" />
                Export
              </Button>
              <Button
                variant="destructive"
                size="sm"
                className="h-7"
                onClick={handleBulkDelete}
              >
                <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                Delete
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 ml-auto"
                onClick={() => setSelectedIds(new Set())}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}

          {/* ── Table (bounded height + sticky header; rows from server pagination) ─ */}
          <div className="max-h-[min(70vh,40rem)] overflow-y-auto overscroll-y-contain border-b border-border [&>div]:overflow-visible">
            <Table>
              <TableHeader className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm shadow-[inset_0_-1px_0_0_hsl(var(--border))]">
              <TableRow>
                {/* Checkbox */}
                <TableHead className="w-10 pl-4">
                  <Checkbox
                    checked={allPageSelected}
                    data-state={somePageSelected ? 'indeterminate' : undefined}
                    onCheckedChange={toggleAll}
                    aria-label="Select all"
                  />
                </TableHead>
                {/* Contact (sortable) */}
                <TableHead>
                  <button
                    className="flex items-center text-xs font-medium uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
                    onClick={() => handleSort('name')}
                  >
                    Contact <SortIcon col="name" />
                  </button>
                </TableHead>
                {/* Email (sortable, toggleable) */}
                {visibleCols.has('email') && (
                  <TableHead>
                    <button
                      className="flex items-center text-xs font-medium uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
                      onClick={() => handleSort('email')}
                    >
                      Email <SortIcon col="email" />
                    </button>
                  </TableHead>
                )}
                {visibleCols.has('phone') && (
                  <TableHead className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Phone</TableHead>
                )}
                {visibleCols.has('location') && (
                  <TableHead className="hidden sm:table-cell text-xs font-medium uppercase tracking-wider text-muted-foreground">Location</TableHead>
                )}
                {visibleCols.has('convs') && (
                  <TableHead className="text-center text-xs font-medium uppercase tracking-wider text-muted-foreground">Convs</TableHead>
                )}
                {visibleCols.has('created') && (
                  <TableHead className="hidden sm:table-cell">
                    <button
                      className="flex items-center text-xs font-medium uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
                      onClick={() => handleSort('created_at')}
                    >
                      Created <SortIcon col="created_at" />
                    </button>
                  </TableHead>
                )}
                {/* Actions */}
                <TableHead className="pr-4 w-24" />
              </TableRow>
            </TableHeader>

            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <SkeletonRow key={i} cols={visibleCols.size + 2} />
                ))
              ) : contacts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleCols.size + 3}>
                    <div className="flex flex-col items-center justify-center py-14 text-center">
                      <div className="rounded-full bg-muted p-4 mb-3 ring-1 ring-border">
                        <Users className="h-7 w-7 text-muted-foreground" />
                      </div>
                      <p className="text-sm font-medium text-muted-foreground">
                        {searchText ? 'No contacts match your search' : 'No contacts yet'}
                      </p>
                      <p className="text-xs text-muted-foreground/70 mt-1">
                        {searchText ? 'Try a different search term' : 'Add your first contact to get started'}
                      </p>
                      {!searchText && (
                        <Button size="sm" className="mt-4" onClick={openCreateDialog}>
                          <Plus className="h-4 w-4 mr-1.5" />
                          Add Contact
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                contacts.map((contact) => (
                  <TableRow key={contact.id} className="group">
                    {/* Checkbox */}
                    <TableCell className="pl-4 w-10">
                      <Checkbox
                        checked={selectedIds.has(contact.id)}
                        onCheckedChange={() => toggleOne(contact.id)}
                        aria-label={`Select ${contact.name || 'contact'}`}
                      />
                    </TableCell>

                    {/* Contact */}
                    <TableCell>
                      <div className="flex items-center gap-2.5">
                        <Avatar className="h-8 w-8 shrink-0">
                          <AvatarImage src={contact.avatar_url} alt={contact.name} />
                          <AvatarFallback className="text-xs font-medium">
                            {getInitials(contact.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-sm font-medium leading-tight">
                            {contact.name || 'Unnamed Contact'}
                          </p>
                          <p className="text-xs text-muted-foreground">#{contact.id}</p>
                        </div>
                      </div>
                    </TableCell>

                    {/* Email */}
                    {visibleCols.has('email') && (
                      <TableCell>
                        <span className={cn('text-sm', !contact.email && 'text-muted-foreground')}>
                          {contact.email || '—'}
                        </span>
                      </TableCell>
                    )}

                    {/* Phone */}
                    {visibleCols.has('phone') && (
                      <TableCell>
                        <span className={cn('text-sm', !contact.phone_number && 'text-muted-foreground')}>
                          {contact.phone_number || '—'}
                        </span>
                      </TableCell>
                    )}

                    {/* Location */}
                    {visibleCols.has('location') && (
                      <TableCell className="hidden sm:table-cell">
                        <div className="flex items-center gap-1 text-sm">
                          {(contact.city || contact.country) && (
                            <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          )}
                          <span className={cn(!(contact.city || contact.country) && 'text-muted-foreground')}>
                            {[contact.city, contact.country].filter(Boolean).join(', ') || '—'}
                          </span>
                        </div>
                      </TableCell>
                    )}

                    {/* Conversations */}
                    {visibleCols.has('convs') && (
                      <TableCell className="text-center">
                        <Badge
                          variant={contact.conversations_count > 0 ? 'default' : 'secondary'}
                          className="tabular-nums"
                        >
                          {contact.conversations_count}
                        </Badge>
                      </TableCell>
                    )}

                    {/* Created */}
                    {visibleCols.has('created') && (
                      <TableCell className="hidden sm:table-cell">
                        <span className="text-sm text-muted-foreground">
                          {formatDate(contact.created_at)}
                        </span>
                      </TableCell>
                    )}

                    {/* Actions */}
                    <TableCell className="pr-4">
                      <div className="flex items-center justify-end gap-1">
                        {/* Hover quick actions */}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                          aria-label={`View ${contact.name || 'contact'}`}
                          onClick={() => openViewSheet(contact)}
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                          aria-label={`Edit ${contact.name || 'contact'}`}
                          onClick={() => openEditDialog(contact)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        {/* Overflow menu */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-7 w-7">
                              <MoreVertical className="h-3.5 w-3.5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openViewSheet(contact)}>
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openEditDialog(contact)}>
                              <Pencil className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => {
                                setEmailOutreachContact(contact);
                                setEmailOutreachOpen(true);
                              }}
                            >
                              <Mail className="h-4 w-4 mr-2" />
                              Send Email
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => {
                                setSelectedContact(contact);
                                setWhatsappInitiatorOpen(true);
                              }}
                            >
                              <MessageSquare className="h-4 w-4 mr-2" />
                              Start WhatsApp Chat
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onClick={() => openDeleteDialog(contact)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </div>

          {/* ── Pagination bar ──────────────────────────────────────────── */}
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-border shrink-0">
            {/* Left: rows per page */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span className="hidden sm:inline">Rows per page</span>
              <Select
                value={String(rowsPerPage)}
                onValueChange={(v) => { setRowsPerPage(parseInt(v, 10)); setPage(0); }}
              >
                <SelectTrigger className="h-7 w-[72px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                  <SelectItem value="-1">All</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Right: count + nav */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span className="tabular-nums">
                {rowsPerPage > 0
                  ? total === 0
                    ? '0 of 0'
                    : `${page * rowsPerPage + 1}–${Math.min((page + 1) * rowsPerPage, total)} of ${total.toLocaleString()}`
                  : `${total.toLocaleString()} total`}
              </span>
              <div className="flex items-center gap-0.5">
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => goTo(0)} disabled={page === 0}>
                  <ChevronsLeft className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => goTo(page - 1)} disabled={page === 0}>
                  <ChevronLeft className="h-3.5 w-3.5" />
                </Button>
                <span className="min-w-[5rem] text-center text-xs tabular-nums">
                  Page {page + 1} of {totalPages}
                </span>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => goTo(page + 1)} disabled={page + 1 >= totalPages}>
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => goTo(totalPages - 1)} disabled={page + 1 >= totalPages}>
                  <ChevronsRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Create Contact Dialog ────────────────────────────────────────── */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Contact</DialogTitle>
          </DialogHeader>
          <ContactForm formData={formData} onChange={handleFormChange} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateContact}>Create Contact</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Edit Contact Dialog ──────────────────────────────────────────── */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Contact</DialogTitle>
          </DialogHeader>
          <ContactForm formData={formData} onChange={handleFormChange} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateContact}>Update Contact</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── View Contact Sheet ───────────────────────────────────────────── */}
      <Sheet open={viewSheetOpen} onOpenChange={setViewSheetOpen}>
        <SheetContent side="right" className="flex flex-col gap-0 p-0 sm:max-w-md overflow-y-auto">
          {selectedContact && (
            <>
              {/* Sheet header */}
              <div className="flex items-center gap-4 p-6 pb-4">
                <Avatar className="h-14 w-14 shrink-0">
                  <AvatarImage src={selectedContact.avatar_url} alt={selectedContact.name} />
                  <AvatarFallback className="text-base font-semibold">
                    {getInitials(selectedContact.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <SheetTitle className="truncate">
                    {selectedContact.name || 'Unnamed Contact'}
                  </SheetTitle>
                  <SheetDescription>Contact #{selectedContact.id}</SheetDescription>
                </div>
              </div>

              {/* Quick actions */}
              <div className="flex gap-2 px-6 pb-4">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => {
                    setEmailOutreachContact(selectedContact);
                    setEmailOutreachOpen(true);
                    setViewSheetOpen(false);
                  }}
                  disabled={!selectedContact.email}
                >
                  <Mail className="h-3.5 w-3.5 mr-1.5" />
                  Send Email
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => {
                    setWhatsappInitiatorOpen(true);
                    setViewSheetOpen(false);
                  }}
                  disabled={!selectedContact.phone_number}
                >
                  <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
                  WhatsApp
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { openEditDialog(selectedContact); setViewSheetOpen(false); }}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              </div>

              <Separator />

              {/* Contact info */}
              <div className="p-6 space-y-4">
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Contact Information
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-sm">
                    <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className={!selectedContact.email ? 'text-muted-foreground' : ''}>
                      {selectedContact.email || 'No email'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <Phone className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className={!selectedContact.phone_number ? 'text-muted-foreground' : ''}>
                      {selectedContact.phone_number || 'No phone'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className={!(selectedContact.city || selectedContact.country) ? 'text-muted-foreground' : ''}>
                      {[selectedContact.city, selectedContact.country].filter(Boolean).join(', ') || 'No location'}
                    </span>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Activity */}
              <div className="p-6 space-y-3">
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Activity
                </p>
                <div className="flex flex-wrap gap-2">
                  <Badge variant={selectedContact.conversations_count > 0 ? 'default' : 'secondary'}>
                    {selectedContact.conversations_count} Conversation{selectedContact.conversations_count !== 1 ? 's' : ''}
                  </Badge>
                  <Badge variant="outline">
                    Joined {formatDate(selectedContact.created_at)}
                  </Badge>
                </div>
              </div>

              {/* Custom attributes */}
              {Object.keys(selectedContact.custom_attributes || {}).length > 0 && (
                <>
                  <Separator />
                  <div className="p-6 space-y-3">
                    <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Custom Attributes
                    </p>
                    <div className="space-y-2">
                      {Object.entries(selectedContact.custom_attributes).map(([k, v]) => (
                        <div key={k} className="flex items-start justify-between text-sm gap-4">
                          <span className="text-muted-foreground capitalize shrink-0">{k.replace(/_/g, ' ')}</span>
                          <span className="text-right font-medium break-all">{String(v)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* ── Delete Confirmation Dialog ───────────────────────────────────── */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Delete Contact
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete{' '}
            <span className="font-medium text-foreground">
              {selectedContact?.name || 'this contact'}
            </span>
            ? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteContact}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── WhatsApp Conversation Initiator ─────────────────────────────── */}
      <ConversationInitiator
        open={whatsappInitiatorOpen}
        onClose={() => { setWhatsappInitiatorOpen(false); setSelectedContact(null); }}
        onConversationCreated={(conversationId) => {
          setWhatsappInitiatorOpen(false);
          setSelectedContact(null);
          toast({ title: 'WhatsApp conversation started', description: `Conversation #${conversationId} created.` });
        }}
        preselectedContact={
          selectedContact
            ? { id: selectedContact.id, name: selectedContact.name || '', phone_number: selectedContact.phone_number || '' }
            : undefined
        }
      />
    </div>
  );
};

export default ContactsPage;
